import React, { useState, useRef, useEffect, memo } from "react";
import Header from "./Header";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { discardSentenceForUserTTS, getRejectedSentencesInALot, getSentencesInALotTTS, rejectSentenceForUserTTS, resolveSentenceForUserTTS, reviewSentenceForStudentTTS, submitWAVForUser, validateSentenceForReviewerTTS, validateWAVForUser } from "@/store/thunks/studentTTT";
import apiClient from "@/services/api/apiClientStudent";
import { BASE_URL } from "@/utils/constants";
import { PlayCircle } from "lucide-react";
import GenericTableStudent from "../GenericTableStudent";

const ReviewModal = memo(function ReviewModal({
  isOpen,
  row,
  comment,
  onCommentChange,
  selectedTargetName,
  onClose,
  onSubmit,
  translatedText,
  loading
}) {
  if (!isOpen || !row) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Add Review</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target ({selectedTargetName})
              </label>
              <p className="p-3 bg-gray-50 rounded-lg text-gray-800 whitespace-pre-wrap">
                {row.original.sourceText}
              </p>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Review / Comment <span className="text-red-500"> (Comment must contain at least 8 words.) *</span>
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none"
                rows="5"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder="Write your feedback, suggestions, or corrections here..."
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={loading}
              className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition disabled:opacity-70"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const CommentsModal = memo(function CommentsModal({
  isOpen,
  comments = [],
  onClose,
  loading = false
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h3 className="text-2xl font-bold text-gray-800">Review Comments</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-3xl font-light hover:scale-110 transition"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-lg">No review comments yet.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {comments.map((c, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {c.studentUsername?.[0]?.toUpperCase() || "S"}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {c.studentUsername || "Unknown Student"}
                        </p>
                        <p className="text-sm text-amber-600 font-medium">
                          Target: {c.targetName || "Unknown"}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                      {new Date(c.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </span>
                  </div>

                  <div className="mt-3 pl-13">
                    <p className="text-gray-800 whitespace-pre-wrap leading-relaxed bg-white p-4 rounded-lg border-l-4 border-amber-400">
                      {c.comment}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const PlayAudioModal = ({ isOpen, audioUrl, loading, error, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.stopPropagation()}
      // This line makes background non-interactive
      style={{ pointerEvents: 'none' }}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-gray-200 animate-[fadeIn_0.3s_ease]"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-light hover:scale-110 transition"
        >
          ×
        </button>

        <div className="text-center">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <PlayCircle className="w-12 h-12 text-white" />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mb-2">Play Recording</h3>
          <p className="text-gray-600 mb-8">Listen to the recorded audio</p>

          {loading && (
            <div className="py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading audio...</p>
            </div>
          )}

          {error && (
            <div className="py-12 text-center">
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          )}

          {audioUrl && !loading && !error && (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <audio
                controls
                autoPlay
                className="w-full h-12"
                style={{ outline: "none" }}
              >
                <source src={audioUrl} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          <button
            onClick={onClose}
            className="mt-8 w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transition transform hover:scale-105"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ASR_TTS = () => {
  const dispatch = useDispatch();
  const [pageIndex, setPageIndex] = useState(0);
  const { selectedLotId, selectedTargetId, selectedTargetName, studentIdForTTS, showMarkedSentencesTTS, editSentences } = useSelector((state) => state.studentDashboard);
  const [pendingAction, setPendingAction] = useState(null); // null | "validate" | "reject" | "resolve"
  const { userId, role, token } = useSelector((state) => state.studentAuth);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [activeTab, setActiveTab] = useState("active"); // active | rejected
  const [doubleClickedRow, setDoubleClickedRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const editedRowsRef = useRef(new Set());
  // Helper to check if row is dirty (edited after validation)
  const isRowEdited = (sentenceId) => editedRowsRef.current.has(sentenceId);
  const [dataVersion, setDataVersion] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const activeValidationToast = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingIndex, setRecordingIndex] = useState(null);
  const [showPlaybackModal, setShowPlaybackModal] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [playModal, setPlayModal] = useState({
    isOpen: false,
    audioUrl: null,
    loading: true,
    error: null
  });

  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    row: null,        // the row being reviewed
    comment: ""       // user's review comment
  });

  const [commentsModal, setCommentsModal] = useState({
    isOpen: false,
    sentenceId: null,
    comments: [],
    loading: false
  });

  const [tableData, setTableData] = useState([]);
  const [files, setFiles] = useState([]);

  // Add this line near your other useState
  const tableDataRef = React.useRef(tableData);

  // Add this effect — THIS IS THE KEY!
  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  // ← ADD THIS HOOK (anywhere inside TTTCard)
  useEffect(() => {
    const isAnyModalOpen =
      reviewModal.isOpen ||
      commentsModal.isOpen ||
      isRecording ||
      showPlaybackModal || playModal.isOpen || !!pendingAction; // ← ADD THIS!

    if (isAnyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [reviewModal.isOpen, commentsModal.isOpen, isRecording, showPlaybackModal, playModal.isOpen, pendingAction]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      clearTimeout(recordTimeoutRef.current);
    };
  }, []);

  const paginatedData = React.useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return files.slice(start, end);
  }, [files, pageIndex, pageSize]);

  useEffect(() => {
    setHasNextPage((pageIndex + 1) * pageSize < files.length);
  }, [pageIndex, pageSize, files.length]);



  const getsentencesForUser = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getSentencesInALotTTS(selectedLotId, selectedTargetId, studentIdForTTS));
      if (res.data && Array.isArray(res.data)) {
        const freshData = res.data;
        setFiles(freshData);
        setTableData(
          freshData.map(item => ({
            ...item,
            targetText: item.translatedText || "",
            editableSourceText:
              item.studentStatus === "REVIEWED"
                ? item.sourceText   // ✅ store original text for editing
                : null,
            originalSourceText: item.sourceText // ✅ keep backup copy
          }))
        );
        setDataVersion(v => v + 1); // ← Force update
      } else {
        setFiles([]);
        setTableData([]);
      }
    } catch (err) {
      console.log(err)
      toast.error("Failed to load sentences.");
    } finally {
      setLoading(false);
    }
  };

  const getRejectedSentencesForUser = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getRejectedSentencesInALot(selectedLotId, selectedTargetId, studentIdForTTS));
      if (res.data && Array.isArray(res.data)) {
        const freshData = res.data;
        setFiles(freshData);
        setTableData(freshData.map(item => ({
          ...item,
          targetText: item.translatedText || ""
        })));
        setDataVersion(v => v + 1); // ← Force update
      } else {
        setFiles([]);
        setTableData([]);
      }
    } catch (err) {
      toast.error("Failed to load rejected sentences.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data from API
  useEffect(() => {
    if (activeTab === "active") {
      getsentencesForUser();
    } else if (activeTab === "rejected") {
      getRejectedSentencesForUser();
    }
  }, [dispatch, selectedLotId, selectedTargetId, activeTab]);


  const handleRecordAudio = async (index) => {
    if (isRecording && recordingIndex === index) {
      // Stop current recording
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearTimeout(recordTimeoutRef.current);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        const chunks = [];

        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
          clearTimeout(recordTimeoutRef.current);
          const audioBlob = new Blob(chunks, { type: "audio/wav" });
          if (audioBlob.type !== "audio/wav") {
            console.error("Not a valid WAV file");
          } else {
            console.log("Audio is WAV format");
          }

          const url = URL.createObjectURL(audioBlob);
          setRecordedAudioUrl(url);
          setShowPlaybackModal(true);

          const updated = [...tableData];
          updated[index].audioBlob = audioBlob;
          setTableData(updated);
          stream.getTracks().forEach(track => track.stop());
        };

        mediaRecorder.start();
        mediaRecorderRef.current = mediaRecorder;
        setIsRecording(true);
        setRecordingIndex(index);

        // ✅ AUTO STOP AFTER 40 SECONDS
        recordTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
          }
        }, 40000);
      } catch (err) {
        console.error("Microphone error:", err);
        alert("Microphone access denied or unavailable.");
      }
    }
  };

  const handleReplay = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };

  const handleReRecord = () => {
    setShowPlaybackModal(false);
    setRecordedAudioUrl(null);
    handleRecordAudio(recordingIndex);
  };

  const reviewSentence = React.useCallback((row) => {
    const absoluteIndex = pageIndex * pageSize + row.index;
    const currentText = tableDataRef.current[absoluteIndex]?.targetText || "";


    setReviewModal({
      isOpen: true,
      row,
      comment: ""
    });
  });

  const handleCommentChange = (newComment) => {
    setReviewModal(prev => ({ ...prev, comment: newComment }));
  };

  const closeRecordingModal = () => {
    setIsRecording(false);
  };

  const closeReviewModal = () => {
    setReviewModal({ isOpen: false, row: null, comment: "" });
  };

  const handleReviewSubmit = async () => {
    if (!reviewModal.comment.trim()) {
      toast.error("Please write a review comment.");
      return;
    }

    setLoading(true);
    try {
      const absoluteIndex = pageIndex * pageSize + reviewModal.row.index;
      const translatedText = tableDataRef.current[absoluteIndex]?.targetText || "";

      const payload = {
        sentenceId: reviewModal.row.original.sentenceId,
        studentId: userId,
        sourceId: reviewModal.row.original.sourceId,
        comment: reviewModal.comment.trim(),
      };

      const res = await dispatch(reviewSentenceForStudentTTS(payload));

      if (res.success) {
        toast.success("Review submitted successfully!");
        await getsentencesForUser();
        setReviewModal({ isOpen: false, row: null, comment: "" });
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      console.log(err)
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showReviewComments = React.useCallback(async (row) => {
    const sentenceId = row.original.sentenceId;

    setCommentsModal({
      isOpen: true,
      sentenceId,
      comments: [],
      loading: true
    });

    try {
      const url = role === 'STUDENT' ? `api/student/ts/comments?sentenceId=${sentenceId}` : `api/reviewer/ts/comments?sentenceId=${sentenceId}`;
      // Replace this with your real API endpoint
      const response = await apiClient(BASE_URL + url);
      // const data = await response.json();
      console.log(response)

      setCommentsModal(prev => ({
        ...prev,
        comments: response.data || [],
        loading: false
      }));
    } catch (err) {
      toast.error("Failed to load comments.");
      setCommentsModal(prev => ({ ...prev, loading: false }));
    }
  });


  const validateSentence = async (row) => {
    const status = row.original.studentStatus;
    const sentenceId = row.original.sentenceId;

    if (status === 'PENDING') {
      toast.info("Please submit a recording before validation.");
      return;
    }


    activeValidationToast.current = sentenceId; // Track current validation

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
            Do you want to SUBMIT this recording?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleValidate();
              }}
              className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Yes, submit
            </button>
            <button
              onClick={() => {
                activeValidationToast.current = null; // Reset
                toast.dismiss("validation-toast"); // Dismiss this toast
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        toastId: "validation-toast", // ← THIS IS KEY: Only one toast with this ID
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center",
        hideProgressBar: true,
      }
    );

    const handleValidate = async () => {
      setLoading(true);
      try {

        const res = role === 'REVIEWER' ? await dispatch(validateSentenceForReviewerTTS(row.original.sentenceId, row.original.sourceId, studentIdForTTS)) : await dispatch(validateWAVForUser(row.original.sentenceId, row.original.sourceId));
        console.log(res)
        if (role === 'REVIEWER' && res.reviewerStatus === 'VALIDATED') {
          toast.success("Sentence recording submitted!", {
            position: "top-center",
            autoClose: 2500,
          }); // Refresh data
        }
        else if (role !== 'REVIEWER' && res.studentStatus === 'VALIDATED') {
          toast.success("Sentence recording submitted!", {
            position: "top-center",
            autoClose: 2500,
          });// Refresh data
        }
        await getsentencesForUser();
      } catch (err) {
        console.error("Error validating sentence:", err);
        toast.error(`Error: ${err.message || "Validation failed"}`, {
          position: "top-center",
        });
      } finally {
        setLoading(false);
        activeValidationToast.current = null; // Reset after done
      }
    };
  };

  const uploadAudio = async (audioBlob, sentenceId, index) => {
    if (!audioBlob || !sentenceId) {
      toast.error("Missing audio or sentence ID");
      return false;
    }

    const formData = new FormData();
    formData.append("file", audioBlob, `sentence_${sentenceId}.wav`);
    formData.append("sentenceId", sentenceId);
    formData.append("studentId", userId);
    formData.append("sourceId", selectedTargetId);

    try {
      setLoading(true);
      const response = await dispatch(submitWAVForUser(formData, sentenceId, selectedTargetId));

      if (response.studentStatus === 'SAVED') {
        toast.success("Audio uploaded successfully!");

        // Optional: Update table to show "uploaded" state
        const updated = [...tableData];
        updated[index].isUploaded = true;
        updated[index].audioStatus = "uploaded";
        setTableData(updated);

        return true;
      } else {
        toast.error('Error Occured!')
      }
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(err.response?.data?.message || "Failed to upload audio");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const discardSentence = async (row) => {
    setPendingAction("discard");

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md sm:text-base font-medium">
            Do you want to mark this sentence as resolved?
          </p>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                closeToast();
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>

            <button
              onClick={async () => {
                closeToast();

                setLoading(true);
                setError(null);

                try {
                  const absoluteIndex =
                    pageIndex * pageSize + row.index;

                  const res = await dispatch(
                    discardSentenceForUserTTS(
                      row.original.sentenceId,
                      row.original.sourceId,
                      studentIdForTTS
                    )
                  );

                  if (res.success) {
                    toast.success(
                      "🎉 Sentence discarded successfully!",
                      {
                        position: "top-center",
                        autoClose: 2000,
                      }
                    );

                    await getsentencesForUser();

                    setDataVersion((v) => v + 1);

                    setDoubleClickedRow(null);

                  } else {
                    toast.error(`⚠️ Failed: ${res.message}`, {
                      position: "top-center",
                    });
                  }

                } catch (err) {
                  console.error("❌ Error discard sentence:", err);

                  toast.error(
                    "⚠️ Failed to discard. Try again.",
                    {
                      position: "top-center",
                    }
                  );

                  setDoubleClickedRow(null);

                } finally {
                  setLoading(false);
                }
              }}
              className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Confirm
            </button>
          </div>
        </div>
      ),
      {
        position: "top-center",
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
      }
    );
  };

  const markAsResolved = async (row) => {
    setPendingAction("markAsResolved");

    // 🟠 Ask user for confirmation
    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md sm:text-base font-medium">
            Do you want to mark this sentence as resolved?
          </p>

          <div className="flex gap-3 justify-center">
            {/* YES BUTTON */}
            <button
              onClick={async () => {
                closeToast();
                await handleMarkAsResolved();
              }}
              className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              ✅ Yes, mark as resolved
            </button>

            {/* CANCEL BUTTON */}
            <button
              onClick={() => {
                closeToast();
                setPendingAction(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center",
        hideProgressBar: true,
      }
    );

    // 🔹 Helper: Actual API call
    const handleMarkAsResolved = async () => {
      setLoading(true);
      setError(null);

      try {
        const absoluteIndex = pageIndex * pageSize + row.index;

        const payload = {
          sentenceId: row.original.sentenceId,
          userId: userId,
          targetId: row.original.targetId,
          lotId: row.original.lotId,
          sourceText: row.original.sourceText
        };

        const res = await dispatch(resolveSentenceForUserTTS(studentIdForTTS, payload));

        if (res.success) {
          toast.success("🎉 Sentence marked as resolved successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          await getsentencesForUser();
          setDataVersion(v => v + 1); // ← Force remount
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        console.error("❌ Error resolving sentence:", err);
        toast.error("⚠️ Failed to mark as resolved. Try again.", {
          position: "top-center",
        });
      } finally {
        setLoading(false);
        setPendingAction(null);
      }
    };
  };

  const rejectSentence = async (row) => {

    setPendingAction("reject");

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md font-medium">
            Do you want to reject this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleReject(row);
              }}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-sm shadow-sm"
            >
              Yes, Reject
            </button>
            <button
              onClick={() => {
                closeToast();
                setPendingAction(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { toastId: "reject-toast", autoClose: false, closeOnClick: false, position: "top-center", hideProgressBar: true }
    );

    const handleReject = async (row) => {
      setLoading(true);
      try {
        const res = await dispatch(rejectSentenceForUserTTS(row.original.sentenceId, row.original.sourceId, studentIdForTTS));
        if (res.success) {
          toast.success("Sentence rejected successfully!");
          await getsentencesForUser();
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        toast.error("Failed to reject.");
      } finally {
        setLoading(false);
        setPendingAction(null);
      }
    };
  };

  const fileColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
      },
      {
        header: `Source Text (${selectedTargetName})`,
        cell: ({ row }) => (
          <span
            onClick={() => {
              if (row.original.sentenceColor === 'blue') {
                showReviewComments(row);
              }
            }}
            className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === "blue"
              ? "text-blue-500 cursor-pointer "
              : row.original.sentenceColor === "green"
                ? "text-green-500"
                : "text-gray-800"
              }`}
          >
            {row.original.sourceText}
          </span>
        ),
        meta: { width: "65%" },
      },
      {
        header: "Target Audio (Record Here)",
        cell: ({ row }) => {
          const index = row.index;
          const isRowRecording = isRecording && recordingIndex === index;
          const isUploaded = tableData[index]?.isUploaded;
          const audioBase64 = row.original.audioUrl;

          // Function to play the base64 WAV audio
          const playRecording = async (studentId, recordingId) => {
            if (!studentId || !recordingId) {
              toast.error("Missing recording info");
              return;
            }

            setPlayModal({
              isOpen: true,
              audioUrl: null,
              loading: true,
              error: null
            });

            try {
              const url = `${BASE_URL}api/student/ts/recording?studentId=${studentId}&recordingId=${recordingId}`;

              // Add authentication if needed
              const response = await fetch(url, {
                credentials: "include",
                headers: {
                  "Authorization": `Bearer ${token}`, // if you have token
                }
              });

              if (!response.ok) throw new Error("Failed to load audio");

              const blob = await response.blob();
              const audioUrl = URL.createObjectURL(blob);

              setPlayModal(prev => ({
                ...prev,
                audioUrl,
                loading: false
              }));
            } catch (err) {
              console.error("Play audio error:", err);
              setPlayModal(prev => ({
                ...prev,
                loading: false,
                error: "Could not play recording. Please try again."
              }));
            }
          };

          return (
            <div className="flex flex-col items-start gap-2">
              <div className="flex flex-col gap-2">
                {console.log(row.original.studentStatus !== 'REVIEWED' || showMarkedSentencesTTS)}
                {(row.original.studentStatus === 'PENDING' || row.original.studentStatus === 'SAVED' || showMarkedSentencesTTS) && <button
                  onClick={() => handleRecordAudio(index)}
                  disabled={isRowRecording}
                  className={`px-4 py-2 rounded-md font-semibold text-white shadow-md transition-all
              ${isRowRecording
                      ? "bg-red-600"
                      : isUploaded
                        ? "bg-green-600"
                        : "bg-[#ec7026] hover:bg-orange-600"
                    }`}
                >
                  {isRowRecording ? "Stop Recording" : isUploaded ? "Uploaded" : "Record Audio"}
                </button>}
                {isUploaded && (
                  <span className="text-xs text-green-600 font-medium">
                    Uploaded successfully
                  </span>
                )}
              </div>

              {/* Play Button - Only show if audio exists */}
              {audioBase64 && (
                <button
                  onClick={() => playRecording(userId, row.original.recordingId)}
                  className={`w-[140px] h-[40px] flex items-center justify-center gap-2 rounded-md text-white font-medium shadow-md transition-all bg-blue-600 hover:bg-blue-700 active:scale-95`}
                >
                  <PlayCircle className="w-5 h-5" />
                  Play Recording
                </button>
              )}
            </div>
          );
        },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED"
                ? "text-green-600"
                : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "10%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED"
                  ? "text-green-600"
                  : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      {
        header: "Actions",
        cell: ({ row }) => (
          role === 'REVIEWER' ?
            <div className="flex flex-col gap-2">
              {row.original.studentStatus === 'REVIEWED' ?
                <>
                  <button onClick={() => discardSentence(row)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                    Discard Sentence
                  </button>
                  <button
                    onClick={() => markAsResolved(row)}
                    className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                  >
                    Mark as Resolved
                  </button>
                </>
                :
                <>
                  <button onClick={() => validateSentence(row)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                    {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit Recording" : "Validate"}
                  </button>
                  <button
                    onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                  >Reject
                  </button>
                </>
              }
            </div>
            :
            <div className="flex flex-col gap-2">
              {(row.original.studentStatus === 'SAVED') && <button onClick={() => validateSentence(row)}
                disabled={row.original.studentStatus === 'VALIDATED'}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.studentStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                {row.original.studentStatus === 'VALIDATED' ? 'Validated' : row.original.studentStatus === 'SAVED' ? "Submit Recording" : "Validate"}
              </button>}
              {(row.original.studentStatus === 'PENDING') && <button
                onClick={() => reviewSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
              >Add Review</button>}
            </div>

        ),
        meta: { width: "10%" },
      },
    ]

    return columns.filter(Boolean);
  }, [
    pageIndex,
    pageSize,
    selectedTargetName,
    isRecording,
    recordingIndex,
    validateSentence,
    reviewSentence,
    handleRecordAudio,
    showReviewComments
  ]);

  const reviewerFileColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },

      {
        header: "Source Text (English)",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const value =
            tableDataRef.current[absoluteIndex]?.editableSourceText ??
            row.original.sourceText;

          if (row.original.studentStatus === 'REVIEWED') {
            return (
              <textarea
                rows={3}
                className="min-h-[60px] w-full border border-gray-300 rounded-md text-gray-700 p-2
      focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none resize-y"
                value={value}
                onChange={(e) => {
                  const newValue = e.target.value;

                  setTableData(prev => {
                    const updated = [...prev];
                    updated[absoluteIndex] = {
                      ...updated[absoluteIndex],
                      editableSourceText: newValue,
                    };
                    return updated;
                  });

                }}
                placeholder="Edit source text..."
              />
            )
          }
          return (
            <span onClick={() => {
              if (row.original.sentenceColor === 'blue') {
                showReviewComments(row);
              }
            }}
              className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer` : row.original.sentenceColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
              {row.original.sourceText}
            </span>
          )
        },
      },
      {
        header: "Target Audio (Record Here)",
        cell: ({ row }) => {
          const audioBase64 = row.original.audioUrl;

          const playRecording = async (studentId, recordingId) => {
            if (!studentId || !recordingId) {
              toast.error("Missing recording info");
              return;
            }

            setPlayModal({
              isOpen: true,
              audioUrl: null,
              loading: true,
              error: null
            });

            try {
              const url = `${BASE_URL}api/reviewer/ts/recording?reviewerId=${studentId}&recordingId=${recordingId}`;

              // Add authentication if needed
              const response = await fetch(url, {
                credentials: "include",
                headers: {
                  "Authorization": `Bearer ${token}`, // if you have token
                }
              });

              if (!response.ok) throw new Error("Failed to load audio");

              const blob = await response.blob();
              const audioUrl = URL.createObjectURL(blob);

              setPlayModal(prev => ({
                ...prev,
                audioUrl,
                loading: false
              }));
            } catch (err) {
              console.error("Play audio error:", err);
              setPlayModal(prev => ({
                ...prev,
                loading: false,
                error: "Could not play recording. Please try again."
              }));
            }
          };

          return (
            <div className="flex flex-col items-center gap-2">
              {row.original.studentStatus === 'REVIEWED' &&
                <span className="block w-full text-center">Not Available</span>
              }
              {/* Play Button - Only show if audio exists */}
              {audioBase64 && (
                <button
                  onClick={() => playRecording(userId, row.original.recordingId)}
                  className={`w-[140px] h-[40px] flex items-center justify-center gap-2 rounded-md text-white font-medium shadow-md transition-all bg-blue-600 hover:bg-blue-700 active:scale-95`}
                >
                  <PlayCircle className="w-5 h-5" />
                  Play Recording
                </button>
              )}
            </div>
          );
        },
        meta: { width: "15%" },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED"
                ? "text-green-600"
                : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "10%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED"
                  ? "text-green-600"
                  : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      {
        header: "Actions",
        cell: ({ row }) => (
          role === 'REVIEWER' ?
            <div className="flex flex-col gap-2">
              {row.original.studentStatus === 'REVIEWED' ?
                <>
                  <button onClick={() => discardSentence(row)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                    Discard Sentence
                  </button>
                  <button
                    onClick={() => markAsResolved(row)}
                    className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                  >
                    Mark as Resolved
                  </button>
                </>
                :
                <>
                  <button onClick={() => validateSentence(row)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                    {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit Recording" : "Validate"}
                  </button>
                  {row.original.reviewerStatus === 'PENDING' && <button
                    onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                  >Reject
                  </button>}
                </>
              }
            </div>
            :
            <div className="flex flex-col gap-2">
              {(row.original.studentStatus === 'SAVED') && <button onClick={() => validateSentence(row)}
                disabled={row.original.studentStatus === 'VALIDATED'}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.studentStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                {row.original.studentStatus === 'VALIDATED' ? 'Validated' : row.original.studentStatus === 'SAVED' ? "Submit Recording" : "Validate"}
              </button>}
              {(row.original.studentStatus === 'PENDING') && <button
                onClick={() => reviewSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
              >Add Review</button>}
            </div>

        ),
        meta: { width: "10%" },
      },
    ]

    return columns.filter(Boolean);
  }, [
    pageIndex,
    pageSize,
    selectedTargetName,
    isRecording,
    recordingIndex,
    validateSentence,
    reviewSentence,
    handleRecordAudio,
    showReviewComments
  ]);

  const rejectedColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Source Text (English)",
        cell: ({ row }) => (
          <span onClick={() => {
            if (row.original.sentenceColor === 'blue') {
              showReviewComments(row);
            }
          }}
            className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer` : row.original.sentenceColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
            {row.original.sourceText}
          </span>
        ),
        meta: { width: "35%" },
      },
      {
        header: "Target Audio (Record Here)",
        cell: ({ row }) => {
          const index = row.index;
          const isRowRecording = isRecording && recordingIndex === index;
          const isUploaded = tableData[index]?.isUploaded;
          const audioBase64 = row.original.audioUrl;

          // Function to play the base64 WAV audio
          const playRecording = async (studentId, recordingId) => {
            if (!studentId || !recordingId) {
              toast.error("Missing recording info");
              return;
            }

            setPlayModal({
              isOpen: true,
              audioUrl: null,
              loading: true,
              error: null
            });

            try {
              const url = role !== 'REVIEWER' ? `${BASE_URL}api/student/ts/recording?studentId=${studentId}&recordingId=${recordingId}` : `${BASE_URL}api/reviewer/ts/recording?reviewerId=${userId}&recordingId=${recordingId}`;

              // Add authentication if needed
              const response = await fetch(url, {
                credentials: "include",
                headers: {
                  "Authorization": `Bearer ${token}`, // if you have token
                }
              });

              if (!response.ok) throw new Error("Failed to load audio");

              const blob = await response.blob();
              const audioUrl = URL.createObjectURL(blob);

              setPlayModal(prev => ({
                ...prev,
                audioUrl,
                loading: false
              }));
            } catch (err) {
              console.error("Play audio error:", err);
              setPlayModal(prev => ({
                ...prev,
                loading: false,
                error: "Could not play recording. Please try again."
              }));
            }
          };

          return (
            <div className="flex flex-col items-start gap-2 items-center">

              {/* Play Button - Only show if audio exists */}
              {audioBase64 && (
                <button
                  onClick={() => playRecording(userId, row.original.recordingId)}
                  className={`w-[140px] h-[40px] flex items-center justify-center gap-2 rounded-md text-white font-medium shadow-md transition-all bg-blue-600 hover:bg-blue-700 active:scale-95`}
                >
                  <PlayCircle className="w-5 h-5" />
                  Play Recording
                </button>
              )}
            </div>
          );
        },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED" || row.original.studentStatus === "RESOLVED"
                ? "text-green-600"
                : row.original.studentStatus === "REJECTED"
                  ? "text-red-600"
                  : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "15%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED" || row.original.reviewerStatus === "RESOLVED"
                  ? "text-green-600"
                  : row.original.reviewerStatus === "REJECTED"
                    ? "text-red-600"
                    : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      role !== "REVIEWER" ? {
        header: "Actions",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const isSelected = absoluteIndex === selectedRow;

          const sentenceId = row.original.sentenceId;
          const isValidated = row.original.studentStatus === 'VALIDATED';
          const isEdited = isRowEdited(sentenceId);           // ← uses ref
          const shouldBeActive = !isValidated || isEdited;

          return (
            role === 'REVIEWER' ?
              <div className="flex flex-col gap-2">
                {row.original.sentenceColor === 'blue' ?
                  <>
                    <button onClick={() => discardSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                      Discard Sentence
                    </button>
                    <button
                      onClick={() => markAsResolved(row)}
                      className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                    >
                      Mark as Resolved
                    </button>
                  </>
                  :
                  <>
                    <button onClick={() => validateSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                    </button>
                    <button
                      onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                    >Reject
                    </button>
                  </>
                }
              </div>
              :
              <div className="flex flex-col gap-2">
                <button onClick={() => validateSentence(row)}
                  disabled={!shouldBeActive || !editSentences}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${!shouldBeActive || !editSentences ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                  {shouldBeActive ? 'Validate' : 'Validated'}
                </button>
              </div>
          );
        }
      } : null
    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, role]);

  return (
    <div className="bg-[#fffaf7] min-h-screen">
      <Header />
      <div className="flex p-4">

        {/* Main Content */}
        <main
          className={`flex-1 pl-4 pr-4 bg-[#fffaf7] transition-all duration-300`}
        >
          <div className="bg-white shadow-lg rounded-2xl p-4 m-2 w-full">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-800">
                  Translation Dashboard (Text to Speech)
                </h2>
                {/* Discard Action Bar - appears on double click */}
                {doubleClickedRow && (
                  <button
                    onClick={() => {
                      setDiscardModal({
                        isOpen: true,
                        row: doubleClickedRow,
                        loading: false
                      });
                    }}
                    className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-md transition transform hover:scale-105"
                  >
                    Discard Sentence
                  </button>
                )}
              </div>
            </div>

            {showMarkedSentencesTTS && (
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setActiveTab("active")}
                  className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "active"
                    ? "bg-amber-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Sentences
                </button>

                <button
                  onClick={() => setActiveTab("rejected")}
                  className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "rejected"
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Sentences marked as issue
                </button>
              </div>
            )}


            {/* Table */}
            <div className="border border-gray-200 rounded-xl">
              <GenericTableStudent
                data={paginatedData}
                columns={role === 'REVIEWER' ? activeTab === 'active' ? reviewerFileColumns : rejectedColumns : activeTab === 'active' ? fileColumns : rejectedColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Recording Popup */}
      {isRecording && !showPlaybackModal && recordingIndex !== null && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/60 z-50 flex items-center justify-center">
          <div
            className="relative bg-white rounded-2xl shadow-2xl p-8 w-[450px] border border-orange-200 animate-[slideDown_0.4s_ease]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={closeRecordingModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>

            <div className="mb-5 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
              <p className="text-sm font-semibold text-orange-700 mb-1">Read this aloud in {selectedTargetName}:</p>
              <p className="text-lg font-medium text-gray-800 whitespace-pre-wrap">
                {tableData[recordingIndex]?.sourceText || "Loading..."}
              </p>
            </div>

            {/* Recording indicator */}
            <div className="flex flex-col items-center mb-6">
              <div className="flex gap-2 mb-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-3 h-3 bg-red-600 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  ></div>
                ))}
              </div>
              <p className="text-lg font-bold text-red-600">Recording...</p>
              <p className="text-sm text-gray-500 mt-1">Auto-stop in 40s</p>
            </div>

            <button
              onClick={() => handleRecordAudio(recordingIndex)}
              className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-lg transition hover:scale-105 active:scale-95"
            >
              Stop Recording
            </button>
          </div>
        </div>
      )}

      {/* Playback Popup */}
      {showPlaybackModal && recordedAudioUrl && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/60 z-50 flex items-center justify-center">
          <div
            className="relative bg-white rounded-2xl shadow-2xl p-8 w-[450px] border border-green-200 
      animate-[fadeIn_0.4s_ease]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPlaybackModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>

            <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
              🔊 Review Recording
            </h3>

            <div className="mb-5">
              <audio ref={audioRef} src={recordedAudioUrl} controls className="w-full rounded-lg shadow" />
            </div>

            <div className="flex justify-between gap-4">
              <button
                onClick={handleReplay}
                className="w-1/2 py-2 border border-gray-300 rounded-xl hover:bg-gray-100 transition"
              >
                Replay
              </button>
              <button
                onClick={handleReRecord}
                className="w-1/2 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl transition"
              >
                Re-record
              </button>
              <button
                onClick={async () => {
                  if (!recordingIndex && recordingIndex !== 0) {
                    toast.error("No recording found");
                    setShowPlaybackModal(false);
                    return;
                  }

                  const currentRow = tableData[recordingIndex];
                  const audioBlob = currentRow.audioBlob;
                  const sentenceId = currentRow.sentenceId;

                  if (!audioBlob) {
                    toast.error("No audio to upload");
                    return;
                  }

                  const success = await uploadAudio(audioBlob, sentenceId, recordingIndex);

                  if (success) {
                    setShowPlaybackModal(false);
                    setRecordedAudioUrl(null);
                    // Optional: refresh data
                    await getsentencesForUser();
                  }
                }}
                disabled={loading}
                className={`px-8 py-3 font-bold rounded-xl shadow-lg transition transform hover:scale-105 flex items-center gap-2 ${loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    Confirm & Save
                  </>
                )}
              </button>
            </div>
            {/* Tip */} <p className="text-center text-xs text-gray-500 mt-5"> Tip: You can replay as many times as needed </p>
          </div>
        </div>
      )}


      <ReviewModal
        isOpen={reviewModal.isOpen}
        selectedTargetName={selectedTargetName}
        row={reviewModal.row}
        comment={reviewModal.comment}
        translatedText={
          reviewModal.row
            ? tableDataRef.current[pageIndex * pageSize + reviewModal.row.index]?.targetText || ""
            : ""
        }
        onCommentChange={handleCommentChange}
        onClose={closeReviewModal}
        onSubmit={handleReviewSubmit}
        loading={loading}
      />
      <CommentsModal
        isOpen={commentsModal.isOpen}
        comments={commentsModal.comments}
        loading={commentsModal.loading}
        onClose={() => setCommentsModal({ isOpen: false, sentenceId: null, comments: [], loading: false })}
      />

      <PlayAudioModal
        isOpen={playModal.isOpen}
        audioUrl={playModal.audioUrl}
        loading={playModal.loading}
        error={playModal.error}
        onClose={() => {
          setPlayModal({ isOpen: false, audioUrl: null, loading: false, error: null });
          if (playModal.audioUrl) URL.revokeObjectURL(playModal.audioUrl);
        }}
      />
    </div>
  );
};

export default ASR_TTS;
